import React from "react";

import ReactMarkdown from "react-markdown";

import { Container, Paper, Typography, Box } from "@mui/material";

import { componentStyles, gradients } from "../styles/themeStyles";

const policy = `## INTRODUCTION

Microcumulus takes your privacy seriously. We created this Privacy Policy to explain how we collect, use, share, and protect your Information when you use our services ("Services"). Last updated: May 15, 2025.

### INFORMATION WE COLLECT

We collect different types of information:

1. Personal Information: Your full name, contact information, and your payment details if you're using paid features.
2. Usage Information: Data from your interaction with our services, including logs, device information, etc.
3. Technical Information: Browser type, IP address, time zone, and cookies that are installed on your device.

### HOW WE USE YOUR INFORMATION

We primarily use your information to provide and enhance the Services. For example, we use:

1. Personal Information for account setup, transaction processing, user support, product updates, etc.
2. Usage Information to troubleshoot and protect against errors, frauds, identity theft, etc.
3. Technical Information to improve our website functionality, analyze usage patterns, and optimize user experience.

### HOW WE SHARE YOUR INFORMATION

We do not sell, rent or share your personal data with third parties outside Microcumulus without your consent, except to operate Services, comply with law, or protect our rights.

### DATA PROCESSING

We process your data based on:
1. Contract performance (to provide our services)
2. Legal obligations
3. Legitimate interests (e.g., to improve our services)
4. Your consent, where applicable

### INTERNATIONAL TRANSFERS

Your information may be processed in countries different from where you live. We ensure appropriate safeguards are in place when transferring data internationally, including standard contractual clauses.

### COOKIE POLICY

We use cookies and similar technologies to:
1. Essential cookies: Necessary for our site to function
2. Analytics cookies: Help us understand how you use our site
3. Marketing cookies: Used to deliver relevant advertisements

You can manage cookie preferences through your browser settings.

### DATA RETENTION

We retain your information only as long as necessary to provide our services and for legitimate business purposes, or as required by law.

### DATA SECURITY

We employ robust security measures to protect your data. These measures include encryption of your data in transit and at rest, frequent software/security updates, and regular monitoring for vulnerabilities.

### YOUR RIGHTS AND CHOICES

You have several rights in relation to your personal information:

1. Access: Obtain a copy of your personal data.
2. Modify: Request to edit inaccurate information.
3. Delete: Request us to delete your data per applicable laws.
4. Limit: Limit our use of your data.
5. Opt-out: Withdraw consent for data collection at any time.
6. Portability: Request the transfer of your data to another service.

### CHILDREN'S PRIVACY

Our services are not directed at individuals under 16. We do not knowingly collect personal information from children. If you believe we have inadvertently collected data from a child, please contact us immediately.

### CHANGES TO THIS POLICY

We may update our privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "last updated" date at the top of this page.

### CONTACT US

If you have any questions about this privacy policy, please contact us at privacy@microcumulus.com.

We are committed to protect your privacy while providing you with world-class cloud services with best-in-class open source software and practices. Thank you for choosing Microcumulus.
`;

export const PrivacyPolicy = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Paper elevation={0} sx={componentStyles.pageHeader}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 2,
            ...gradients.text.style,
          }}
        >
          Privacy Policy
        </Typography>
        <Typography variant="body1" color="text.secondary">
          How we collect, use, and protect your information.
        </Typography>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            "& h2": {
              fontSize: "1.5rem",
              fontWeight: 600,
              mb: 2,
              mt: 4,
              "&:first-of-type": {
                mt: 0,
              },
            },
            "& h3": {
              fontSize: "1.25rem",
              fontWeight: 600,
              mb: 2,
              mt: 3,
            },
            "& p": {
              mb: 2,
            },
            "& a": {
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline",
              },
            },
            "& strong": {
              fontWeight: 600,
            },
            "& ol, & ul": {
              paddingLeft: 3,
              mb: 2,
            },
            "& li": {
              mb: 1,
            },
          }}
        >
          <ReactMarkdown>{policy}</ReactMarkdown>
        </Box>
      </Paper>
    </Container>
  );
};
